import { Checkbox, DatePicker, Flex, Form, Input, Modal } from "antd";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router";
import SelectNationality from "~components/SelectNationality";
import SelectPersonGender from "~components/SelectPersonGender";
import SelectPersonStatus from "~components/SelectPersonStatus";
import { PersonStatuses } from "~enums/personStatuses";
import { Sports } from "~enums/sports";
import { useCreatePerson } from "~hooks/apiHooks/useCreatePerson";
import { noLeadingOrTrailingSpacesRule } from "~utils/noLeadingOrTrailingSpacesRule.ts";
import type { AddPersonForm } from "./types/AddPersonForm";

type Props = {
  open: boolean;
  handleClose: () => void;
  onAdd?: (localFullName: string, personId: string) => void;
};

const AddPersonModal = ({ open, handleClose, onAdd }: Props) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm<AddPersonForm>();
  const { mutateAsync: createPerson, isPending: isFormUploading } = useCreatePerson();
  const [resetAfterSubmit, setResetAfterSubmit] = useState(false);
  const { sport } = useParams();
  const isHockey = sport === Sports.Hockey;

  const handleSubmit = async (personData: AddPersonForm) => {
    const { data } = await createPerson({
      ...personData,
      ...(personData.dob && { dob: personData.dob.format("YYYY-MM-DD") }),
      ...(isHockey && personData?.nationality && { additionalDetails: { representation: personData.nationality } }),
    });

    if (resetAfterSubmit) {
      form.resetFields();
      setResetAfterSubmit(false);
    } else {
      const personId = data[0]?.personId;
      if (!personId) {
        return;
      }
      handleClose();
      onAdd?.(personData.nameFullLocal, personId);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={500}
      destroyOnHidden
      title={formatMessage({ id: "add.person" })}
      okText={formatMessage({ id: "add" })}
      onOk={form.submit}
      okButtonProps={{ disabled: isFormUploading, loading: isFormUploading }}
      cancelButtonProps={{ disabled: isFormUploading }}
      closable={!isFormUploading}
      footer={(originNode) => (
        <Flex align="center" justify="space-between">
          <Checkbox checked={resetAfterSubmit} onChange={(e) => setResetAfterSubmit(e.target.checked)}>
            <FormattedMessage id="add.person.another" />
          </Checkbox>
          <Flex align="center" gap={8}>
            {originNode}
          </Flex>
        </Flex>
      )}
    >
      <Form
        clearOnDestroy
        form={form}
        onFinish={handleSubmit}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        initialValues={{ status: PersonStatuses.Active }}
      >
        <Form.Item<AddPersonForm>
          name="nameFullLocal"
          label={formatMessage({ id: "name.local.full" })}
          tooltip={formatMessage({ id: "add.person.form.help.local.full.name" })}
          rules={[
            {
              required: true,
              message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "name.local.full" }) }),
            },
            {
              min: 2,
              message: formatMessage({ id: "form.validation.min.length" }, { length: 2 }),
            },
            noLeadingOrTrailingSpacesRule(formatMessage),
          ]}
        >
          <Input disabled={isFormUploading} />
        </Form.Item>
        <Form.Item<AddPersonForm>
          name="gender"
          label={formatMessage({ id: "gender" })}
          tooltip={formatMessage({ id: "add.person.form.help.gender" })}
          getValueFromEvent={(option?: { value: string; label: string } | string) => (typeof option === "string" ? option : option?.value)}
          rules={[
            {
              required: true,
              message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "gender" }) }),
            },
          ]}
        >
          <SelectPersonGender disabled={isFormUploading} />
        </Form.Item>
        <Form.Item<AddPersonForm>
          name="status"
          label={formatMessage({ id: "status" })}
          tooltip={formatMessage({ id: "add.person.form.help.status" })}
          getValueFromEvent={(option?: { value: string; label: string } | string) => (typeof option === "string" ? option : option?.value)}
          rules={[
            {
              required: true,
              message: formatMessage({ id: "form.help.required.field" }, { field: formatMessage({ id: "status" }) }),
            },
          ]}
        >
          <SelectPersonStatus disabled={isFormUploading} />
        </Form.Item>
        <Form.Item<AddPersonForm>
          name="nationality"
          label={formatMessage({ id: "nationality" })}
          tooltip={formatMessage({ id: "add.person.form.help.nationality" })}
        >
          <SelectNationality disabled={isFormUploading} />
        </Form.Item>
        <Form.Item<AddPersonForm>
          name="dob"
          label={formatMessage({ id: "date.of.birth" })}
          tooltip={formatMessage({ id: "add.person.form.help.dob" })}
        >
          <DatePicker format="DD/MM/YYYY" placeholder="" disabled={isFormUploading} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddPersonModal;
